import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import localizationService, { LocalizationConfig } from '../services/localizationService'

interface LocalizationContextType {
  config: LocalizationConfig | null
  isLoading: boolean
  changeLanguage: (language: string) => Promise<void>
  formatPrice: (priceInCents?: number) => string
  t: (key: string, options?: any) => string
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined)

export const useLocalization = () => {
  const context = useContext(LocalizationContext)
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider')
  }
  return context
}

interface LocalizationProviderProps {
  children: React.ReactNode
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<LocalizationConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { t: i18nT } = useTranslation()

  useEffect(() => {
    initializeLocalization()
  }, [])

  const initializeLocalization = async () => {
    try {
      setIsLoading(true)
      console.log('🌍 Initialisation de la localisation...')
      
      const localizationConfig = await localizationService.initialize()
      setConfig(localizationConfig)
      
      console.log('✅ Localisation initialisée:', localizationConfig)
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation de la localisation:', error)
      
      // Fallback vers la configuration par défaut
      const fallbackConfig = await localizationService.detectLocalization()
      setConfig(fallbackConfig)
    } finally {
      setIsLoading(false)
    }
  }

  const changeLanguage = async (language: string) => {
    try {
      console.log('🌍 Changement de langue vers:', language)
      await localizationService.changeLanguage(language)
      
      const newConfig = await localizationService.getCurrentConfig()
      setConfig(newConfig)
      
      console.log('✅ Langue changée, nouvelle config:', newConfig)
    } catch (error) {
      console.error('❌ Erreur lors du changement de langue:', error)
    }
  }

  const formatPrice = (priceInCents?: number): string => {
    if (!config) return '$49.99'
    
    const price = priceInCents || config.price
    return localizationService.formatPrice(price, config.currency, config.locale)
  }

  const t = (key: string, options?: any): string => {
    return i18nT(key, { 
      trialPrice: formatPrice(100),
      price: formatPrice(), // monthly price
      ...options 
    }) as string
  }

  const value: LocalizationContextType = {
    config,
    isLoading,
    changeLanguage,
    formatPrice,
    t
  }

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  )
}

export default LocalizationProvider
